import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  Clock, AlertTriangle, RefreshCw, Send, CheckCircle, XCircle, Eye,
  CalendarClock, Users, ShieldAlert, Search, ChevronRight, X, Calendar,
  Pencil, Ban, Play, Plus, History, CreditCard, ArrowUpRight, Copy,
  ExternalLink, Zap, Link2, Wrench, PauseCircle, PlayCircle, ChevronLeft,
  Loader2, AlertCircle, BadgePercent, Trash2
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

// ── Constants ──

const FILTERS = [
  { value: "all", label: "All Users", icon: Users },
  { value: "active", label: "Active", icon: CheckCircle },
  { value: "expiring_soon", label: "Expiring Soon", icon: Clock },
  { value: "past_due", label: "Past Due", icon: AlertCircle },
  { value: "expired", label: "Expired", icon: XCircle },
  { value: "canceled", label: "Canceled", icon: Ban },
  { value: "no_auto_renew", label: "Auto-Renew OFF", icon: AlertTriangle },
  { value: "manual_billing", label: "Manual Billing", icon: CreditCard },
  { value: "no_stripe_sub", label: "No Stripe Sub", icon: ShieldAlert },
  { value: "billing_mismatch", label: "Mismatch", icon: Zap },
  { value: "custom_plans", label: "Custom Plans", icon: CalendarClock },
];

const REMINDER_TYPES = [
  { value: "expiring_soon", label: "Expiring Soon" },
  { value: "expired", label: "Plan Expired" },
  { value: "renewal", label: "Renewal Reminder" },
  { value: "payment_failed", label: "Payment Failed" },
];

const STATUSES = ["active", "expired", "canceled", "suspended", "trialing", "past_due", "incomplete", "unpaid", "paused"];
const INTERVALS = [
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

const STATUS_COLORS = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  trialing: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  past_due: "bg-orange-500/15 text-orange-400 border-orange-500/20",
  canceled: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  cancelled: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  expired: "bg-red-500/15 text-red-400 border-red-500/20",
  incomplete: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  unpaid: "bg-red-500/15 text-red-400 border-red-500/20",
  paused: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  unknown: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
};

const FLAG_CONFIG = {
  NO_STRIPE_SUB: { label: "No Stripe", color: "bg-rose-500/15 text-rose-400 border-rose-500/20" },
  STATUS_MISMATCH: { label: "Mismatch", color: "bg-amber-500/15 text-amber-400 border-amber-500/20" },
  MANUAL_BILLING: { label: "Manual", color: "bg-blue-500/15 text-blue-400 border-blue-500/20" },
  RENEW_OFF: { label: "Renew OFF", color: "bg-orange-500/15 text-orange-400 border-orange-500/20" },
  CUSTOM_PLAN: { label: "Custom", color: "bg-purple-500/15 text-purple-400 border-purple-500/20" },
  EXPIRING_SOON: { label: "Expiring", color: "bg-amber-500/15 text-amber-400 border-amber-500/20" },
  NEEDS_REVIEW: { label: "Review", color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20" },
};

const getAuthConfig = () => {
  const t = localStorage.getItem("token");
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
};

// Safe date formatter — date-fns `format` THROWS on invalid dates, which
// white-screens the page. Any unparseable value renders as "—" instead.
const fmtDate = (iso, pattern = "MMM d, yyyy") => {
  if (!iso) return "—";
  try {
    return format(typeof iso === "string" ? parseISO(iso) : iso, pattern);
  } catch {
    return "—";
  }
};

// Relative timestamp for Last Sync column — "2m ago", "3h ago", "5d ago", or "Never"
const formatRelativeSync = (iso) => {
  if (!iso) return "Never";
  try {
    const d = typeof iso === "string" ? parseISO(iso) : iso;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return "just now";
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return format(d, "MMM d, yyyy");
  } catch {
    return "—";
  }
};

// Human label for a discount active on a Stripe subscription
const subDiscountLabel = (d) => {
  if (!d) return "";
  try {
    const val = d.percent_off ? `${d.percent_off}% off` : `$${d.amount_off} off`;
    const dur = d.duration === "once" ? "next payment only"
      : d.duration === "repeating" ? `${d.duration_in_months || "?"} months`
      : "forever";
    const end = Number.isFinite(d.end) ? ` · until ${fmtDate(new Date(d.end * 1000))}` : "";
    return `${val} · ${dur}${end}`;
  } catch {
    return "";
  }
};

// ── Info Card ──

function InfoCard({ label, value, icon: Icon, valueClass = "text-foreground", mono = false }) {
  return (
    <div className="bg-muted/20 rounded-lg border border-theme/30 p-2.5">
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3 text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-sm font-semibold ${mono ? "font-mono text-xs" : "capitalize"} ${valueClass}`}>{value || "—"}</p>
    </div>
  );
}

// ── User Detail Panel ──

// Error boundary — a render error inside the detail panel must show a readable
// message instead of white-screening the whole admin page.
class PanelErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="panel-error">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={this.props.onClose} />
          <div className="relative w-full max-w-xl bg-background border-l border-theme p-6 space-y-3">
            <p className="text-sm font-semibold text-red-500">Couldn't display this account</p>
            <p className="text-xs text-muted-foreground break-all">{String(this.state.error?.message || this.state.error)}</p>
            <Button size="sm" variant="outline" onClick={this.props.onClose} data-testid="panel-error-close">Close</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function UserDetailPanel({ user, plans, onClose, onRefresh }) {
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Edit state
  const [changePlan, setChangePlan] = useState(user.plan_id || "");
  const [changeInterval, setChangeInterval] = useState(user.billing_interval || "month");
  const [changeStatus, setChangeStatus] = useState(user.status || "");
  const [extendDays, setExtendDays] = useState("");
  const [notes, setNotes] = useState("");
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [promoCode, setPromoCode] = useState("");

  // Discount state
  const [discounts, setDiscounts] = useState([]);
  const [discountMode, setDiscountMode] = useState("");
  const [customType, setCustomType] = useState("percentage");
  const [customValue, setCustomValue] = useState("");
  const [durationChoice, setDurationChoice] = useState("once");
  const [customMonths, setCustomMonths] = useState("6");
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  useEffect(() => { loadHistory(); loadDetails(); }, [user.user_id]);

  useEffect(() => {
    axios.get(`${API}/admin/discounts`, getAuthConfig())
      .then(res => setDiscounts((res.data.discounts || []).filter(d => d.active !== false)))
      .catch(() => {});
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/billing/${user.user_id}/history`, getAuthConfig());
      setHistory(res.data.logs || []);
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  };

  const loadDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/billing/${user.user_id}/details`, getAuthConfig());
      setDetails(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const act = async (endpoint, body, successMsg) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/billing/${endpoint}`, body, getAuthConfig());
      toast.success(res.data.changes?.join(", ") || res.data.message || successMsg);
      setNotes("");
      setExtendDays("");
      loadHistory();
      loadDetails();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally { setSaving(false); }
  };

  const quickExtend = (days) => act("extend", { user_id: user.user_id, days, notes: notes || `Quick +${days}d` });
  const quickCancel = () => act("cancel", { user_id: user.user_id, immediate: cancelImmediate, notes });
  const quickPause = (action) => act("pause-resume", { user_id: user.user_id, action, notes });

  const saveChanges = () => {
    if (changePlan !== user.plan_id || changeInterval !== user.billing_interval) {
      if (user.stripe_subscription_id) {
        act("change-plan", { user_id: user.user_id, new_plan_id: changePlan, billing_interval: changeInterval, notes });
      } else {
        act("update", { user_id: user.user_id, subscription_plan: changePlan, billing_period: changeInterval === "year" ? "annual" : "monthly", notes });
      }
    } else if (changeStatus !== user.status) {
      act("update", { user_id: user.user_id, subscription_status: changeStatus, notes });
    } else if (extendDays) {
      act("extend", { user_id: user.user_id, days: parseInt(extendDays), notes });
    }
  };

  const sendPaymentLink = async () => {
    setSaving(true);
    try {
      const body = {
        user_id: user.user_id, plan_id: changePlan || user.plan_id, billing_interval: changeInterval, notes
      };
      if (promoCode.trim()) body.promo_code = promoCode.trim();
      const res = await axios.post(`${API}/admin/billing/send-payment-link`, body, getAuthConfig());
      const url = res.data.checkout_url;
      if (url) {
        navigator.clipboard.writeText(url).then(() => {
          toast.success("Payment link copied to clipboard!", { duration: 6000 });
        }).catch(() => {
          toast.success("Payment link created — copy it below", { duration: 6000 });
        });
        setPaymentLink(url);
      } else {
        toast.success(res.data.message || "Payment link created");
      }
      loadHistory();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment link");
    } finally { setSaving(false); }
  };
  const fixBilling = () => act("fix-billing", { user_id: user.user_id, plan_id: changePlan || user.plan_id, billing_interval: changeInterval, notes });
  const syncFromStripe = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/billing/${user.user_id}/sync-from-stripe`, {}, getAuthConfig());
      toast.success(res.data.message || "Synced from Stripe");
      if (res.data.changes) {
        res.data.changes.forEach(c => toast.info(c, { duration: 6000 }));
      }
      loadHistory();
      loadDetails();
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to sync from Stripe";
      toast.error(detail, { duration: 10000 });
    } finally { setSaving(false); }
  };
  const sendReminder = (type, channel = "email") => act("send-reminder", { user_id: user.user_id, reminder_type: type, channel });
  const markReviewed = () => act("mark-reviewed", { user_id: user.user_id });

  const discountDurationLabel = (d) =>
    d.duration === "once" ? "first payment only"
    : d.duration === "repeating" ? `${d.duration_months || 12} months`
    : "forever";

  const applyDiscount = async () => {
    setApplyingDiscount(true);
    try {
      const body = { user_id: user.user_id, notes };
      if (discountMode === "custom") {
        body.mode = "custom";
        body.discount_type = customType;
        body.value = parseFloat(customValue);
        if (durationChoice === "once") body.duration = "once";
        else if (durationChoice === "forever") body.duration = "forever";
        else {
          body.duration = "repeating";
          body.duration_months = durationChoice === "custom" ? parseInt(customMonths) : parseInt(durationChoice);
        }
      } else {
        body.mode = "existing";
        body.discount_id = discountMode;
      }
      const res = await axios.post(`${API}/admin/billing/apply-discount`, body, getAuthConfig());
      toast.success(res.data.message || "Discount applied & verified on Stripe", { duration: 8000 });
      setDiscountMode("");
      setCustomValue("");
      loadHistory();
      loadDetails();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply discount", { duration: 10000 });
    } finally { setApplyingDiscount(false); }
  };

  const removeDiscount = () => act("remove-discount", { user_id: user.user_id, notes });

  const activeDiscount = details?.stripe?.discount;
  const selectedDiscount = discounts.find(d => d.discount_id === discountMode);
  const applyDisabled = applyingDiscount || !discountMode ||
    (discountMode === "custom" && (!customValue || parseFloat(customValue) <= 0 ||
      (durationChoice === "custom" && (!customMonths || parseInt(customMonths) < 1))));

  const daysColor = !user.days_remaining && user.days_remaining !== 0 ? "text-muted-foreground"
    : user.days_remaining < 0 ? "text-red-400"
    : user.days_remaining <= 3 ? "text-amber-400"
    : user.days_remaining <= 7 ? "text-yellow-400" : "text-emerald-400";

  const stripe = details?.stripe;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-detail-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-background border-l border-theme overflow-y-auto animate-in slide-in-from-right-full duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-theme p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-foreground font-['Manrope']">{user.name || user.email}</h2>
              <p className="text-xs text-muted-foreground">{user.email}</p>
              {user.company_name && <p className="text-xs text-muted-foreground/70">{user.company_name}</p>}
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose} data-testid="close-panel-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-3">
            {["overview", "actions", "history"].map(t => (
              <Button key={t} variant={tab === t ? "default" : "ghost"} size="sm"
                className={`h-7 text-xs ${tab === t ? "bg-[#0046BE] text-white" : "text-muted-foreground"}`}
                onClick={() => setTab(t)} data-testid={`tab-${t}`}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* ── Overview Tab ── */}
          {tab === "overview" && (
            <>
              <div className="grid grid-cols-2 gap-3" data-testid="status-overview">
                <InfoCard label="Plan" value={user.plan_name || user.plan_id} icon={CreditCard} />
                <InfoCard label="Status" value={user.status} icon={user.is_expired ? XCircle : user.is_past_due ? AlertCircle : CheckCircle}
                  valueClass={STATUS_COLORS[user.status]?.split(" ")[1] || "text-foreground"} />
                <InfoCard label="Price" value={user.plan_price ? `$${user.plan_price}/${user.billing_interval === "year" ? "yr" : "mo"}` : "—"} icon={CreditCard} />
                <InfoCard label="Auto-Renew" value={user.auto_renew ? "ON" : user.stripe_subscription_id ? "OFF" : "N/A"} icon={RefreshCw}
                  valueClass={user.auto_renew ? "text-emerald-400" : user.stripe_subscription_id ? "text-red-400" : "text-muted-foreground"} />
                <InfoCard label="Renewal Date" value={fmtDate(user.current_period_end)} icon={Calendar} />
                <InfoCard label="Days Left" icon={Clock} valueClass={daysColor}
                  value={user.days_remaining === null || user.days_remaining === undefined ? "—"
                    : user.days_remaining < 0 ? `${Math.abs(user.days_remaining)}d overdue`
                    : user.days_remaining === 0 ? "Today" : `${user.days_remaining} days`} />
                <InfoCard label="Payment Status" value={user.last_payment_status || "—"} icon={CreditCard}
                  valueClass={user.last_payment_status === "paid" ? "text-emerald-400" : "text-muted-foreground"} />
                <InfoCard label="Billing" value={user.billing_interval === "year" ? "Yearly" : "Monthly"} icon={ArrowUpRight} />
                <InfoCard label="Last Stripe Sync" value={formatRelativeSync(user.last_stripe_sync_at)} icon={RefreshCw}
                  valueClass={user.last_stripe_sync_at ? "text-foreground" : "text-muted-foreground"} />
              </div>

              {/* Stripe IDs */}
              {(user.stripe_subscription_id || user.stripe_customer_id) && (
                <div className="space-y-2" data-testid="stripe-ids">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Stripe IDs</h3>
                  {user.stripe_subscription_id && (
                    <div className="flex items-center gap-2 text-xs font-mono bg-muted/20 rounded-md p-2 border border-theme/30">
                      <span className="text-muted-foreground">Sub:</span>
                      <span className="text-foreground truncate flex-1">{user.stripe_subscription_id}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(user.stripe_subscription_id)} data-testid="copy-sub-id">
                        <Copy className="w-3 h-3" />
                      </Button>
                      <a href={`https://dashboard.stripe.com/subscriptions/${user.stripe_subscription_id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid="open-stripe-sub"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                    </div>
                  )}
                  {user.stripe_customer_id && (
                    <div className="flex items-center gap-2 text-xs font-mono bg-muted/20 rounded-md p-2 border border-theme/30">
                      <span className="text-muted-foreground">Cust:</span>
                      <span className="text-foreground truncate flex-1">{user.stripe_customer_id}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(user.stripe_customer_id)} data-testid="copy-cust-id">
                        <Copy className="w-3 h-3" />
                      </Button>
                      <a href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid="open-stripe-cust"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Flags */}
              {user.flags?.length > 0 && (
                <div data-testid="flags-section">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Flags</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {user.flags.map(f => {
                      const cfg = FLAG_CONFIG[f] || { label: f, color: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20" };
                      return <Badge key={f} className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>;
                    })}
                  </div>
                </div>
              )}

              {/* Stripe Live Data */}
              {stripe && !stripe.error && (
                <div data-testid="stripe-live-data">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Stripe Live Data</h3>
                  <div className="text-xs space-y-1 bg-muted/10 rounded-md p-3 border border-theme/20">
                    <div className="flex justify-between"><span className="text-muted-foreground">Stripe Status</span><span className="text-foreground">{stripe.status}</span></div>
                    {stripe.price_amount && <div className="flex justify-between"><span className="text-muted-foreground">Price</span><span className="text-foreground">${stripe.price_amount}/{stripe.interval}</span></div>}
                    {stripe.latest_invoice_status && <div className="flex justify-between"><span className="text-muted-foreground">Last Invoice</span><span className="text-foreground">{stripe.latest_invoice_status}</span></div>}
                    {stripe.latest_invoice_amount !== undefined && <div className="flex justify-between"><span className="text-muted-foreground">Last Amount</span><span className="text-foreground">${stripe.latest_invoice_amount}</span></div>}
                    {stripe.discount && (
                      <div className="flex justify-between" data-testid="overview-active-discount">
                        <span className="text-muted-foreground">Active Discount</span>
                        <span className="text-emerald-400">{stripe.discount.name ? `${stripe.discount.name} · ` : ""}{subDiscountLabel(stripe.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between"><span className="text-muted-foreground">Cancel at Period End</span><span className={stripe.cancel_at_period_end ? "text-red-400" : "text-emerald-400"}>{stripe.cancel_at_period_end ? "Yes" : "No"}</span></div>
                  </div>
                </div>
              )}
              {loading && <p className="text-xs text-muted-foreground text-center py-2">Loading Stripe data...</p>}
            </>
          )}

          {/* ── Actions Tab ── */}
          {tab === "actions" && (
            <>
              {/* Quick Actions */}
              <div data-testid="quick-actions">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Quick Extend</h3>
                <div className="flex flex-wrap gap-2">
                  {[7, 30, 90, 365].map(d => (
                    <Button key={d} size="sm" variant="outline" className="h-8 text-xs border-theme" onClick={() => quickExtend(d)} disabled={saving} data-testid={`extend-${d}d-btn`}>
                      <Plus className="w-3 h-3 mr-1" /> +{d === 365 ? "1yr" : `${d}d`}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Status Actions */}
              <div data-testid="status-actions">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Subscription Control</h3>
                <div className="flex flex-wrap gap-2">
                  {user.status !== "active" && (
                    <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => act("update", { user_id: user.user_id, subscription_status: "active", notes })} disabled={saving} data-testid="activate-btn">
                      <Play className="w-3 h-3 mr-1" /> Activate
                    </Button>
                  )}
                  {user.stripe_subscription_id && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={quickCancel} disabled={saving} data-testid="cancel-btn">
                        <Ban className="w-3 h-3 mr-1" /> Cancel {cancelImmediate ? "(Now)" : "(End of Period)"}
                      </Button>
                      <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={cancelImmediate} onChange={e => setCancelImmediate(e.target.checked)} className="w-3 h-3" />
                        Immediate
                      </label>
                      {user.status === "active" && (
                        <Button size="sm" variant="outline" className="h-8 text-xs border-blue-500/30 text-blue-400" onClick={() => quickPause("pause")} disabled={saving} data-testid="pause-btn">
                          <PauseCircle className="w-3 h-3 mr-1" /> Pause
                        </Button>
                      )}
                      {user.status === "paused" && (
                        <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-500/30 text-emerald-400" onClick={() => quickPause("resume")} disabled={saving} data-testid="resume-btn">
                          <PlayCircle className="w-3 h-3 mr-1" /> Resume
                        </Button>
                      )}
                    </>
                  )}
                  {user.flags?.includes("NO_STRIPE_SUB") && (
                    <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={fixBilling} disabled={saving} data-testid="fix-billing-btn">
                      <Wrench className="w-3 h-3 mr-1" /> Fix Billing
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-xs border-theme" onClick={sendPaymentLink} disabled={saving} data-testid="payment-link-btn">
                    <Link2 className="w-3 h-3 mr-1" /> Payment Link
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" onClick={syncFromStripe} disabled={saving} data-testid="sync-stripe-user-btn">
                    <RefreshCw className="w-3 h-3 mr-1" /> Sync Stripe
                  </Button>
                  {user.flags?.some(f => f === "NEEDS_REVIEW" || f === "STATUS_MISMATCH" || f === "NO_STRIPE_SUB") && (
                    <Button size="sm" variant="outline" className="h-8 text-xs border-theme" onClick={markReviewed} data-testid="mark-reviewed-btn">
                      <Eye className="w-3 h-3 mr-1" /> Mark Reviewed
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Input placeholder="Promo / Coupon code (optional)" value={promoCode} onChange={e => setPromoCode(e.target.value)}
                    className="h-8 text-xs border-theme flex-1" data-testid="promo-code-input" />
                  {promoCode && <span className="text-[10px] text-emerald-400">Will be applied to payment link</span>}
                </div>
                {paymentLink && (
                  <div className="mt-2 p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-md" data-testid="payment-link-display">
                    <div className="flex items-center gap-1.5 mb-1">
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                      <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Payment Link Ready (copied to clipboard)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input readOnly value={paymentLink} className="flex-1 text-[10px] font-mono bg-transparent border border-theme/30 rounded px-2 py-1 text-foreground" onClick={e => e.target.select()} />
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentLink); toast.success("Copied!"); }} data-testid="copy-payment-link">
                        <Copy className="w-3 h-3" />
                      </Button>
                      <a href={paymentLink} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid="open-payment-link"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              {/* Discount / Promo */}
              <div data-testid="discount-section">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <BadgePercent className="w-3.5 h-3.5" /> Discount / Promo
                </h3>
                {activeDiscount && (
                  <div className="mb-2.5 p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-md flex items-center justify-between gap-2" data-testid="active-discount-box">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                        <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Active on Stripe Subscription</span>
                      </div>
                      <p className="text-xs text-foreground mt-0.5">
                        {activeDiscount.name ? `${activeDiscount.name} · ` : ""}{subDiscountLabel(activeDiscount)}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                      onClick={removeDiscount} disabled={saving} data-testid="remove-discount-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                )}
                {!user.stripe_subscription_id ? (
                  <p className="text-[11px] text-muted-foreground bg-muted/20 border border-theme/30 rounded-md p-2.5" data-testid="discount-no-sub-note">
                    Requires an active Stripe subscription. Use "Fix Billing" first, or send a payment link with a promo code.
                  </p>
                ) : (
                  <div className="space-y-2.5">
                    <Select value={discountMode} onValueChange={setDiscountMode}>
                      <SelectTrigger className="h-9 border-theme text-sm" data-testid="discount-select">
                        <SelectValue placeholder="Choose a discount to apply..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom one-off discount…</SelectItem>
                        {discounts.map(d => (
                          <SelectItem key={d.discount_id} value={d.discount_id}>
                            {d.name} — {d.discount_type === "percentage" ? `${d.value}%` : `$${d.value}`} off ({discountDurationLabel(d)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedDiscount && (
                      <p className="text-[10px] text-muted-foreground" data-testid="existing-discount-note">
                        Applies with its configured duration: <span className="text-foreground">{discountDurationLabel(selectedDiscount)}</span>
                      </p>
                    )}
                    {discountMode === "custom" && (
                      <div className="space-y-2.5" data-testid="custom-discount-fields">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">Type</label>
                            <Select value={customType} onValueChange={setCustomType}>
                              <SelectTrigger className="h-9 border-theme text-sm" data-testid="custom-discount-type"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                <SelectItem value="fixed">Fixed amount ($)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">{customType === "percentage" ? "Percent off" : "Amount off ($)"}</label>
                            <Input type="number" min="0" max={customType === "percentage" ? 100 : undefined} step="0.01"
                              placeholder={customType === "percentage" ? "e.g. 20" : "e.g. 10"} value={customValue}
                              onChange={e => setCustomValue(e.target.value)} className="h-9 border-theme text-sm" data-testid="custom-discount-value" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">Duration</label>
                            <Select value={durationChoice} onValueChange={setDurationChoice}>
                              <SelectTrigger className="h-9 border-theme text-sm" data-testid="discount-duration-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="once">One time (next payment only)</SelectItem>
                                <SelectItem value="3">3 months</SelectItem>
                                <SelectItem value="6">6 months</SelectItem>
                                <SelectItem value="12">12 months</SelectItem>
                                <SelectItem value="custom">Custom months…</SelectItem>
                                <SelectItem value="forever">Forever</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {durationChoice === "custom" && (
                            <div>
                              <label className="text-[10px] text-muted-foreground block mb-1">Months</label>
                              <Input type="number" min="1" value={customMonths} onChange={e => setCustomMonths(e.target.value)}
                                className="h-9 border-theme text-sm" data-testid="custom-duration-months" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                      onClick={applyDiscount} disabled={applyDisabled} data-testid="apply-discount-btn">
                      {applyingDiscount ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <BadgePercent className="w-3.5 h-3.5 mr-2" />}
                      {applyingDiscount ? "Applying & verifying on Stripe..." : activeDiscount ? "Replace Discount on Subscription" : "Apply Discount to Subscription"}
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center">
                      Auto-applies to the customer's upcoming payments and is verified on Stripe after saving
                    </p>
                  </div>
                )}
              </div>
              <div data-testid="edit-section">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Edit Subscription</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Plan</label>
                      <Select value={changePlan} onValueChange={setChangePlan}>
                        <SelectTrigger className="h-9 border-theme text-sm" data-testid="edit-plan-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {plans.map(p => {
                            const id = typeof p === "string" ? p : p.plan_id;
                            const nm = typeof p === "string" ? p : p.name || p.plan_id;
                            return <SelectItem key={id} value={id}>{nm}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Interval</label>
                      <Select value={changeInterval} onValueChange={setChangeInterval}>
                        <SelectTrigger className="h-9 border-theme text-sm" data-testid="edit-interval-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {INTERVALS.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Status (local)</label>
                      <Select value={changeStatus} onValueChange={setChangeStatus}>
                        <SelectTrigger className="h-9 border-theme text-sm" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Extend by Days</label>
                      <Input type="number" min="1" placeholder="e.g. 30" value={extendDays}
                        onChange={e => setExtendDays(e.target.value)} className="h-9 border-theme text-sm" data-testid="extend-days-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground block mb-1">Admin Notes</label>
                    <Input placeholder="Reason for change..." value={notes} onChange={e => setNotes(e.target.value)}
                      className="h-9 border-theme text-sm" data-testid="notes-input" />
                  </div>
                  <Button className="w-full bg-[#0046BE] text-white h-9" onClick={saveChanges} disabled={saving} data-testid="save-changes-btn">
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Pencil className="w-3.5 h-3.5 mr-2" />}
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                  {user.stripe_subscription_id && (
                    <p className="text-[10px] text-muted-foreground text-center">Plan/interval changes will update Stripe with proration</p>
                  )}
                </div>
              </div>

              {/* Send Reminder */}
              <div data-testid="reminder-section">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Send Reminder</h3>
                <div className="grid grid-cols-2 gap-2">
                  {REMINDER_TYPES.map(r => (
                    <Button key={r.value} size="sm" variant="outline" className="h-8 text-xs border-theme justify-start"
                      onClick={() => sendReminder(r.value)} disabled={saving} data-testid={`send-${r.value}-btn`}>
                      <Send className="w-3 h-3 mr-1.5" /> {r.label}
                    </Button>
                  ))}
                </div>
                {user.last_reminder_sent && (
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Last: {fmtDate(user.last_reminder_sent)} ({user.last_reminder_type?.replace(/_/g, " ")})
                  </p>
                )}
              </div>
            </>
          )}

          {/* ── History Tab ── */}
          {tab === "history" && (
            <div data-testid="history-section">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" /> Change History
              </h3>
              {historyLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
              ) : history.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No admin changes recorded</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {history.map((log, i) => (
                    <div key={i} className="text-xs border border-theme/40 rounded-md p-2.5 bg-muted/10">
                      <div className="text-muted-foreground">{fmtDate(log.timestamp, "MMM d, yyyy h:mm a")}</div>
                      <div className="text-foreground mt-0.5">{(log.changes || []).join(" | ")}</div>
                      {log.notes && <div className="text-muted-foreground/70 mt-0.5 italic">"{log.notes}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──

const AdminSubscriptions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingUserId, setSyncingUserId] = useState(null);
  const [scanningPayments, setScanningPayments] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const debounceRef = useRef(null);

  const fetchData = useCallback(async (f, s, p) => {
    const filter = f ?? activeFilter;
    const search = s ?? debouncedSearch;
    const pg = p ?? page;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/billing/overview`, {
        ...getAuthConfig(),
        params: { filter, search, page: pg, page_size: PAGE_SIZE },
      });
      setData(res.data);
      if (selectedUser) {
        const updated = res.data.users?.find(u => u.user_id === selectedUser.user_id);
        if (updated) setSelectedUser(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load data");
    } finally { setLoading(false); }
  }, [activeFilter, debouncedSearch, page, selectedUser]);

  useEffect(() => { fetchData(); }, [page]);
  useEffect(() => {
    axios.get(`${API}/admin/billing/plans`, getAuthConfig())
      .then(res => setPlans(res.data || [])).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
      fetchData(activeFilter, searchQuery, 1);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  const handleFilterChange = (f) => {
    setActiveFilter(f);
    setPage(1);
    fetchData(f, debouncedSearch, 1);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/billing/sync`, {}, getAuthConfig());
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  // Per-row quick sync — fires the same endpoint used inside the detail panel,
  // so Subscription behavior is identical. Does not alter any sync logic.
  const handleRowSync = async (userId) => {
    setSyncingUserId(userId);
    try {
      const res = await axios.post(`${API}/admin/billing/${userId}/sync-from-stripe`, {}, getAuthConfig());
      toast.success(res.data.message || "Synced from Stripe");
      (res.data.changes || []).forEach(c => toast.info(c, { duration: 5000 }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed", { duration: 8000 });
    } finally { setSyncingUserId(null); }
  };

  const handleScanFix = async () => {
    setScanningPayments(true);
    try {
      const res = await axios.post(`${API}/admin/billing/scan-pending-payments`, {}, getAuthConfig());
      const { fixed, total_scanned, results } = res.data;
      if (fixed > 0) {
        toast.success(`Fixed ${fixed} payment(s) out of ${total_scanned} scanned`);
        results.filter(r => r.action === "fixed").forEach(r => {
          toast.info(`${r.email}: ${r.plan_id} plan activated ($${r.amount})`);
        });
      } else {
        toast.info(res.data.message || "No pending payments need fixing");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Scan failed");
    } finally { setScanningPayments(false); }
  };

  const getDaysLabel = (u) => {
    if (u.days_remaining === null || u.days_remaining === undefined) return "—";
    if (u.days_remaining < 0) return `${Math.abs(u.days_remaining)}d over`;
    if (u.days_remaining === 0) return "Today";
    return `${u.days_remaining}d`;
  };

  const getDaysColor = (u) => {
    const dr = u.days_remaining;
    if (dr === null || dr === undefined) return "text-muted-foreground";
    if (dr < 0) return "text-red-400";
    if (dr <= 3) return "text-amber-400";
    if (dr <= 7) return "text-yellow-400";
    return "text-emerald-400";
  };

  const summary = data?.summary || {};
  const totalPages = data?.total_pages || 1;

  const SUMMARY_CARDS = [
    { key: "total", filter: "all", label: "Total", color: "text-blue-400", bg: "bg-blue-500/20", icon: Users },
    { key: "active", filter: "active", label: "Active", color: "text-emerald-400", bg: "bg-emerald-500/20", icon: CheckCircle },
    { key: "expiring_soon", filter: "expiring_soon", label: "Expiring", color: "text-amber-400", bg: "bg-amber-500/20", icon: Clock },
    { key: "past_due", filter: "past_due", label: "Past Due", color: "text-orange-400", bg: "bg-orange-500/20", icon: AlertCircle },
    { key: "expired", filter: "expired", label: "Expired", color: "text-red-400", bg: "bg-red-500/20", icon: XCircle },
    { key: "canceled", filter: "canceled", label: "Canceled", color: "text-zinc-400", bg: "bg-zinc-500/20", icon: Ban },
    { key: "no_stripe_sub", filter: "no_stripe_sub", label: "No Stripe", color: "text-rose-400", bg: "bg-rose-500/20", icon: ShieldAlert },
    { key: "billing_mismatch", filter: "billing_mismatch", label: "Mismatch", color: "text-amber-400", bg: "bg-amber-500/20", icon: Zap },
  ];

  return (
    <div className="space-y-5" data-testid="admin-subscriptions-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Subscription Management</h1>
          <p className="text-muted-foreground text-sm">Stripe-synced billing control center</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" onClick={handleScanFix} disabled={scanningPayments} data-testid="scan-fix-payments-btn">
            {scanningPayments ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
            {scanningPayments ? "Scanning..." : "Scan & Fix Payments"}
          </Button>
          <Button variant="outline" className="border-theme text-foreground" onClick={handleSync} disabled={syncing} data-testid="sync-stripe-btn">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {syncing ? "Syncing..." : "Sync Stripe"}
          </Button>
          <Button onClick={() => fetchData()} className="bg-[#0046BE] text-white" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {SUMMARY_CARDS.map(({ key, filter, label, color, bg, icon: Icon }) => (
          <Card key={key}
            className={`glass-card border-theme cursor-pointer transition-all hover:scale-[1.02] ${activeFilter === filter ? "ring-1 ring-primary" : ""}`}
            onClick={() => handleFilterChange(filter)} data-testid={`summary-card-${key}`}>
            <CardContent className="p-2.5">
              <div className="flex items-center gap-2 mb-0.5">
                <div className={`w-6 h-6 rounded-md ${bg} flex items-center justify-center`}>
                  <Icon className={`w-3 h-3 ${color}`} />
                </div>
                <span className={`text-lg font-bold ${color}`}>{summary[key] ?? 0}</span>
              </div>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map(f => (
            <Button key={f.value} variant={activeFilter === f.value ? "default" : "outline"} size="sm"
              onClick={() => handleFilterChange(f.value)}
              className={`h-7 text-[11px] ${activeFilter === f.value ? "bg-[#0046BE] text-white" : "border-theme text-foreground"}`}
              data-testid={`filter-btn-${f.value}`}>
              <f.icon className="w-3 h-3 mr-1" /> {f.label}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-[340px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search name, email, Stripe ID..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-8 text-sm border-theme" data-testid="search-input" />
        </div>
      </div>

      {/* Table */}
      <Card className="glass-card border-theme">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="subscriptions-table">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-muted-foreground text-left text-[11px] border-b border-theme">
                  <th className="p-2.5">User</th>
                  <th className="p-2.5">Plan</th>
                  <th className="p-2.5">Price</th>
                  <th className="p-2.5">Interval</th>
                  <th className="p-2.5">Status</th>
                  <th className="p-2.5">Auto-Renew</th>
                  <th className="p-2.5">Renewal Date</th>
                  <th className="p-2.5">Days Left</th>
                  <th className="p-2.5">Payment</th>
                  <th className="p-2.5">Stripe Sub</th>
                  <th className="p-2.5">Last Sync</th>
                  <th className="p-2.5">Flags</th>
                  <th className="p-2.5 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={13} className="p-8 text-center text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...
                  </td></tr>
                ) : (data?.users || []).length === 0 ? (
                  <tr><td colSpan={13} className="p-8 text-center text-muted-foreground">No users match this filter</td></tr>
                ) : (data?.users || []).map(u => (
                  <tr key={u.user_id}
                    className="border-b border-theme/40 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => setSelectedUser(u)} data-testid={`user-row-${u.user_id}`}>
                    <td className="p-2.5">
                      <div className="font-medium text-foreground text-xs max-w-[140px] truncate">{u.name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[140px]">{u.email}</div>
                    </td>
                    <td className="p-2.5">
                      <Badge variant="outline" className="border-theme text-foreground text-[10px] capitalize">{u.plan_name || u.plan_id || "—"}</Badge>
                    </td>
                    <td className="p-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      {u.plan_price ? `$${u.plan_price}` : "—"}
                    </td>
                    <td className="p-2.5 text-xs text-muted-foreground capitalize whitespace-nowrap">
                      {u.billing_interval === "year" ? "Yearly" : "Monthly"}
                    </td>
                    <td className="p-2.5">
                      <Badge className={`text-[10px] ${STATUS_COLORS[u.status] || STATUS_COLORS.unknown}`}>
                        {u.status}
                      </Badge>
                    </td>
                    <td className="p-2.5">
                      {u.stripe_subscription_id ? (
                        <Badge className={`text-[10px] ${u.auto_renew ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-red-500/15 text-red-400 border-red-500/20"}`}>
                          {u.auto_renew ? "ON" : "OFF"}
                        </Badge>
                      ) : <span className="text-[10px] text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(u.current_period_end)}
                    </td>
                    <td className="p-2.5">
                      <span className={`text-xs font-medium ${getDaysColor(u)}`}>{getDaysLabel(u)}</span>
                    </td>
                    <td className="p-2.5 text-xs text-muted-foreground capitalize whitespace-nowrap">
                      {u.last_payment_status || "—"}
                    </td>
                    <td className="p-2.5">
                      {u.stripe_subscription_id ? (
                        <button onClick={e => { e.stopPropagation(); copyToClipboard(u.stripe_subscription_id); }}
                          className="text-[10px] font-mono text-blue-400 hover:text-blue-300 cursor-pointer truncate max-w-[100px] block" title={u.stripe_subscription_id}>
                          {u.stripe_subscription_id.substring(0, 14)}...
                        </button>
                      ) : <span className="text-[10px] text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2.5" data-testid={`last-sync-cell-${u.user_id}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap" title={u.last_stripe_sync_at || "Never"}>
                          {formatRelativeSync(u.last_stripe_sync_at)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-emerald-400 hover:bg-emerald-500/10"
                          disabled={syncingUserId === u.user_id}
                          onClick={e => { e.stopPropagation(); handleRowSync(u.user_id); }}
                          title="Sync from Stripe"
                          data-testid={`row-sync-btn-${u.user_id}`}
                        >
                          <RefreshCw className={`w-3 h-3 ${syncingUserId === u.user_id ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                    </td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-0.5">
                        {(u.flags || []).slice(0, 3).map(f => {
                          const cfg = FLAG_CONFIG[f] || { label: f.replace(/_/g, " "), color: "bg-zinc-500/15 text-zinc-400" };
                          return <Badge key={f} className={`text-[8px] px-1 py-0 ${cfg.color}`}>{cfg.label}</Badge>;
                        })}
                      </div>
                    </td>
                    <td className="p-2.5 text-right">
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground"
                        onClick={e => { e.stopPropagation(); setSelectedUser(u); }} data-testid={`manage-btn-${u.user_id}`}>
                        Manage <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-theme" data-testid="pagination">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages} ({data?.total || 0} users)
              </span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-xs border-theme" disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page-btn">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs border-theme" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Panel */}
      {selectedUser && (
        <PanelErrorBoundary key={selectedUser.user_id} onClose={() => setSelectedUser(null)}>
          <UserDetailPanel
            user={selectedUser}
            plans={plans}
            onClose={() => setSelectedUser(null)}
            onRefresh={() => fetchData()}
          />
        </PanelErrorBoundary>
      )}
    </div>
  );
};

export default AdminSubscriptions;
